import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Upload, Loader2, Home } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Available subjects for selection
const SUBJECT_OPTIONS = [
  'English', 'EAL', 'IELTS', 'Mathematical Methods',
  'Specialist Maths', 'General Maths', 'Further Maths', 'Biology',
  'Chemistry', 'Physics', 'Accounting', 'Economics',
  'Business Management', 'Psychology', 'Legal Studies', 'Other'
];

/**
 * V5 CLEAN REBUILD - TUTOR APPLICATION
 * 
 * Matches live site design exactly
 * Completely fresh implementation isolated from all legacy code.
 */
const TutorApplicationV5 = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    notes: ''
  });
  
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [cvFile, setCvFile] = useState(null);
  const [cvFileName, setCvFileName] = useState('');
  const [fileError, setFileError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const toggleSubject = (subject) => {
    setSelectedSubjects(prev =>
      prev.includes(subject)
        ? prev.filter(s => s !== subject)
        : [...prev, subject]
    );
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (!file) {
      setCvFile(null);
      setCvFileName('');
      setFileError('');
      return;
    }
    
    // Validate file type — accept by extension OR MIME type. Mobile file
    // pickers (iOS/Android) sometimes strip the extension or report a
    // generic `application/octet-stream` type, so we allow the server to
    // do the final verification (magic-byte sniffing) in those cases.
    const allowedExts = ['.pdf', '.doc', '.docx'];
    const allowedMimes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/octet-stream', // allow mobile generic type; server validates
      '', // some mobile browsers send empty type
    ];
    const nameLower = (file.name || '').toLowerCase();
    const hasAllowedExt = allowedExts.some((ext) => nameLower.endsWith(ext));
    const hasAllowedMime = allowedMimes.includes(file.type || '');
    
    if (!hasAllowedExt && !hasAllowedMime) {
      setFileError('Invalid file type. Please upload PDF, DOC, or DOCX');
      setCvFile(null);
      setCvFileName('');
      e.target.value = '';
      return;
    }
    
    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setFileError(`File too large. Max 10MB (current: ${(file.size / (1024 * 1024)).toFixed(1)}MB)`);
      setCvFile(null);
      setCvFileName('');
      e.target.value = '';
      return;
    }
    
    // File is valid
    setCvFile(file);
    setCvFileName(file.name);
    setFileError('');
  };
  
  const validateForm = () => {
    if (!formData.fullName.trim()) {
      toast.error('Please enter your full name');
      return false;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!formData.phone.trim()) {
      toast.error('Please enter your phone number');
      return false;
    }
    if (selectedSubjects.length === 0) {
      toast.error('Please select at least one subject');
      return false;
    }
    if (!cvFile) {
      toast.error('Please upload your CV');
      return false;
    }
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Create FormData for multipart upload
      const formDataToSend = new FormData();
      formDataToSend.append('full_name', formData.fullName.trim());
      formDataToSend.append('email', formData.email.trim().toLowerCase());
      formDataToSend.append('phone', formData.phone.trim());
      formDataToSend.append('subjects', selectedSubjects.join(', '));
      formDataToSend.append('cv_file', cvFile);
      if (formData.notes.trim()) {
        formDataToSend.append('notes', formData.notes.trim());
      }
      
      const response = await fetch(`${BACKEND_URL}/api/v5/tutor-application`, {
        method: 'POST',
        body: formDataToSend
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to submit application');
      }
      
      const result = await response.json();
      
      toast.success('Application submitted successfully!');
      
      // Redirect to success page
      navigate('/tutor-application-success');
      
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-white">
      {/* Top Navigation */}
      <div className="border-b bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex justify-end">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            <Home className="h-4 w-4" />
            Home
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Title */}
        <h1 className="text-5xl font-bold text-gray-900 mb-3">Become a Tutor</h1>
        <p className="text-gray-600 mb-12">
          Join our team of expert tutors. Complete the form below and upload your CV. We'll review and contact you within 48 hours.
        </p>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Personal Information Section */}
          <div className="bg-white border rounded-lg p-8 space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Personal Information</h2>
            
            {/* Full Name */}
            <div>
              <Label htmlFor="fullName" className="text-sm font-medium text-gray-700 mb-2 block">
                Full Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="fullName"
                name="fullName"
                type="text"
                value={formData.fullName}
                onChange={handleInputChange}
                className="w-full"
                disabled={isSubmitting}
              />
            </div>
            
            {/* Email and Phone - Side by Side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-2 block">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700 mb-2 block">
                  Phone <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            
            {/* Subjects - Clickable Buttons */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Subjects You Can Teach <span className="text-red-500">*</span> <span className="text-gray-500 font-normal">(Select all that apply)</span>
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                {SUBJECT_OPTIONS.map((subject) => (
                  <button
                    key={subject}
                    type="button"
                    onClick={() => toggleSubject(subject)}
                    disabled={isSubmitting}
                    className={`px-4 py-2.5 rounded-md border text-sm font-medium transition-all ${
                      selectedSubjects.includes(subject)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {subject}
                  </button>
                ))}
              </div>
            </div>
            
            {/* CV Upload */}
            <div>
              <Label htmlFor="cvFile" className="text-sm font-medium text-gray-700 mb-2 block">
                Upload Your CV / Resume <span className="text-red-500">*</span>
              </Label>
              <p className="text-xs text-gray-500 mb-3">PDF, DOC, or DOCX (Max 10MB)</p>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
                <input
                  id="cvFile"
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={isSubmitting}
                />
                <label htmlFor="cvFile" className="cursor-pointer flex flex-col items-center gap-3">
                  <Upload className="h-10 w-10 text-gray-400" />
                  <div>
                    {cvFileName ? (
                      <p className="text-sm font-medium text-gray-900">{cvFileName}</p>
                    ) : (
                      <p className="text-sm font-medium text-gray-600">Select CV File</p>
                    )}
                  </div>
                </label>
              </div>
              {fileError && (
                <p className="text-red-600 text-sm mt-2">{fileError}</p>
              )}
            </div>
            
            {/* Additional Notes */}
            <div>
              <Label htmlFor="notes" className="text-sm font-medium text-gray-700 mb-2 block">
                Additional Notes (Optional)
              </Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={4}
                maxLength={1000}
                className="w-full resize-none"
                disabled={isSubmitting}
              />
              <p className="text-xs text-gray-500 mt-1">{formData.notes.length}/1000 characters</p>
            </div>
          </div>
          
          {/* What Happens Next */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-2">What happens next?</h3>
            <p className="text-sm text-gray-700">
              We'll review your application within 48 hours and contact you via email or phone to discuss next steps.
            </p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-4">
            <Button
              type="button"
              onClick={() => navigate('/')}
              variant="outline"
              disabled={isSubmitting}
              className="px-6"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !cvFile}
              className="px-6 bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Application'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TutorApplicationV5;
